import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ref as dbRef, set } from 'firebase/database';
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { database, storage } from '../../Firebase/firebaseconfig';
import { ArrowLeft, Monitor, Wand2 } from 'lucide-react';
import PopulateValuesModal from './PopulateValuesModal';
import ConfirmDialog from './ConfirmDialog';
import { compressImage } from './imageCompress';
import './admin-page.css';

function genID() {
  return 'dt' + Math.floor(1000 + Math.random() * 9000);
}
function genFilename(file) {
  const ext = file.name.split('.').pop();
  return `${Date.now()}_${Math.random().toString(36).substr(2, 5)}.${ext}`;
}

const FIELDS = [
  { key: 'Name',      label: 'Product Name',       placeholder: 'e.g. Custom Gaming Tower' },
  { key: 'Price',     label: 'Price (₹)',           placeholder: 'e.g. 35000', type: 'number' },
  { key: 'Processor', label: 'Processor',           placeholder: 'e.g. Intel Core i5-12400' },
  { key: 'Ram',       label: 'RAM',                 placeholder: 'e.g. 16GB DDR4' },
  { key: 'Storage',   label: 'Storage',             placeholder: 'e.g. 512GB NVMe SSD' },
  { key: 'Display',   label: 'Display',             placeholder: 'e.g. 24" FHD Monitor' },
  { key: 'GPU',       label: 'GPU',                 placeholder: 'e.g. NVIDIA GTX 1650' },
  { key: 'OS',        label: 'Operating System',    placeholder: 'e.g. Windows 11 Home' },
];

// Scraped/AI-extracted spec labels are free text with arbitrarily many
// sub-rows per category (e.g. "Processor Brand" / "Processor Name" /
// "Processor Variant" / "Clock Speed" all belong in the one Processor
// field) — matched against these fixed form fields by tokenizing each label
// into whole words and checking word-stem matches, then COLLECTING every
// matching spec for a field and joining them, rather than picking one
// "primary" value and dropping the rest.
//
// Word-boundary tokenization (not raw substring search) matters here:
// "Touchscreen" must not match the "screen" keyword (it's a different word),
// while "Graphics" must still match the "graphic" stem (prefix match).
// `exclude` stops cross-contamination between similarly-worded fields, e.g.
// "Dedicated Graphic Memory Capacity" contains "memory" but is GPU VRAM, not
// system RAM.
const FIELD_RULES = {
  Processor: { include: [['processor'], ['cpu'], ['chipset'], ['clock', 'speed']], exclude: ['graphic', 'gpu'] },
  Ram: { include: [['ram'], ['memory']], exclude: ['graphic', 'gpu', 'video'] },
  Storage: { include: [['storage'], ['hard', 'disk'], ['ssd'], ['hdd'], ['capacity']], exclude: ['ram', 'memory', 'graphic'] },
  Display: { include: [['display'], ['screen'], ['monitor'], ['resolution']], exclude: ['touch'] },
  GPU: { include: [['graphic'], ['gpu']] }, // "graphic" stem also matches "graphics"
  OS: { include: [['operating', 'system'], ['os']] },
};

// Dropped when something more informative is also present for the same
// field (e.g. "SSD: Yes" once "SSD Capacity: 512GB" is also known) — kept
// only as a last resort if nothing else matched.
const SKIP_VALUES = new Set(['yes', 'no', 'n/a', 'na', '-', '—']);

function tokenize(label) {
  return String(label).toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
}

function hasWord(tokens, stem) {
  return tokens.some(t => t.startsWith(stem));
}

function fieldMatches(tokens, rule) {
  if (rule.exclude && rule.exclude.some(stem => hasWord(tokens, stem))) return false;
  return rule.include.some(group => group.every(stem => hasWord(tokens, stem)));
}

function mapSpecsToFields(specs) {
  const collected = {};
  (specs || []).forEach(({ label, value }) => {
    const tokens = tokenize(label);
    for (const [field, rule] of Object.entries(FIELD_RULES)) {
      if (fieldMatches(tokens, rule)) {
        (collected[field] = collected[field] || []).push(String(value).trim());
        break; // a label only ever contributes to the first field it matches
      }
    }
  });

  const mapped = {};
  for (const [field, values] of Object.entries(collected)) {
    const meaningful = values.filter(v => !SKIP_VALUES.has(v.toLowerCase()));
    const list = meaningful.length > 0 ? meaningful : values;
    const seen = new Set();
    mapped[field] = list.filter(v => {
      const k = v.toLowerCase();
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    }).join(' ');
  }
  return mapped;
}

// What the stage-2 AI verifier is asked to map onto — independent of the
// local rule-based FIELD_RULES above, used as a smarter primary source with
// the rules kept as a deterministic fallback for anything it leaves blank.
const AI_TARGET_FIELDS = [
  { key: 'Processor', hint: 'CPU in this exact order: Brand + core tier + model number + generation + speed. Model number always comes right after the core tier, never at the end. Examples: "Intel Core i5-12400 12th Gen 2.5GHz", "AMD Ryzen 5 5600G".' },
  { key: 'Ram',       hint: 'RAM capacity, type, and frequency only (e.g. 16GB DDR4 3200MHz). Never GPU VRAM.' },
  { key: 'Storage',   hint: 'Storage capacity and type (e.g. 512GB NVMe SSD or 1TB HDD)' },
  { key: 'Display',   hint: 'Monitor SIZE and RESOLUTION and panel type ONLY (e.g. 24" 1920x1080 IPS). NEVER touchscreen capability, pointing devices, or any boolean Yes/No feature flag.' },
  { key: 'GPU',       hint: 'Dedicated GPU name and VRAM (e.g. NVIDIA GeForce GTX 1650 4GB). Empty string for integrated-only graphics.' },
  { key: 'OS',        hint: 'Operating system name and edition (e.g. Windows 11 Home)' },
];

export default function AddDesktop() {
  const navigate = useNavigate();
  const [form, setForm] = useState({});
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [productFiles, setProductFiles] = useState([]);
  const [productPreviews, setProductPreviews] = useState([]);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPopulate, setShowPopulate] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const coverInputRef = useRef();
  const prodInputRef = useRef();

  const handleCover = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCoverFile(file);
    setCoverPreview(URL.createObjectURL(file));
  };

  const handlePopulate = (result) => {
    // AI-verified mapping (stage 2, cross-checked by a second model) wins
    // when present and non-empty; the local rule-based mapping is the
    // deterministic fallback for any field the AI left blank rather than guess.
    const ruleMapped = mapSpecsToFields(result.specs);
    const aiMapped = result.mappedFields || {};
    const mapped = {};
    for (const key of Object.keys(ruleMapped)) {
      const aiVal = aiMapped[key];
      mapped[key] = (typeof aiVal === 'string' && aiVal.trim()) ? aiVal.trim() : ruleMapped[key];
    }
    for (const key of Object.keys(aiMapped)) {
      if (mapped[key] === undefined && typeof aiMapped[key] === 'string' && aiMapped[key].trim()) {
        mapped[key] = aiMapped[key].trim();
      }
    }
    setForm(f => ({
      ...f,
      ...(result.name ? { Name: result.name } : {}),
      ...mapped,
    }));
    if (result.coverImageFile) {
      setCoverFile(result.coverImageFile);
      setCoverPreview(URL.createObjectURL(result.coverImageFile));
    }
  };

  const handleProductImages = (e) => {
    const files = Array.from(e.target.files);
    setProductFiles(prev => [...prev, ...files]);
    setProductPreviews(prev => [...prev, ...files.map(f => URL.createObjectURL(f))]);
  };

  const removeProductImage = (i) => {
    setProductFiles(prev => prev.filter((_, idx) => idx !== i));
    setProductPreviews(prev => prev.filter((_, idx) => idx !== i));
  };

  const uploadFile = (file, path) => new Promise((resolve, reject) => {
    const ref = storageRef(storage, path);
    const task = uploadBytesResumable(ref, file);
    task.on('state_changed',
      (snap) => setProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
      reject,
      () => getDownloadURL(task.snapshot.ref).then(resolve).catch(reject)
    );
  });

  const handlePreSubmit = (e) => {
    e.preventDefault();
    if (!form.Name || !form.Price) return setError('Name and Price are required.');
    if (!coverFile) return setError('Cover image is required.');
    setError('');
    setShowConfirm(true);
  };

  const handleSubmit = async () => {
    setShowConfirm(false);
    setError(''); setSuccess(''); setUploading(true);
    try {
      const desktopID = genID();
      const coverC = await compressImage(coverFile);
      const coverName = genFilename(coverC);
      await uploadFile(coverC, `Desktop/${coverName}`);
      const imageNames = [];
      for (const file of productFiles) {
        const compressed = await compressImage(file);
        const fname = genFilename(compressed);
        await uploadFile(compressed, `Desktop/${fname}`);
        imageNames.push(fname);
      }
      const data = { ...form, Price: Number(form.Price), Cover_image: coverName, Images: imageNames.join(',') };
      await set(dbRef(database, `desktop_build/${desktopID}`), data);
      setSuccess('Desktop added successfully!');
      setForm({});
      setCoverFile(null); setCoverPreview(null);
      setProductFiles([]); setProductPreviews([]);
      setProgress(0);
    } catch (err) {
      setError('Upload failed: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="ap-page">
      <div className="ap-header">
        <button className="ap-back-btn" onClick={() => navigate('/admin/dashboard')}><ArrowLeft size={18} /></button>
        <div>
          <h1 className="ap-title">Add Desktop Tower</h1>
          <p className="ap-subtitle">Fill in details and upload images</p>
        </div>
      </div>

      {error && <div className="ap-error">{error}</div>}
      {success && <div className="ap-success">{success}</div>}

      <div className="ap-card">
        <form onSubmit={handlePreSubmit}>
          <div className="ap-row">
            {FIELDS.map(f => (
              <div className="ap-form-group" key={f.key}>
                <label className="ap-label">{f.label}</label>
                <input
                  className="ap-input"
                  type={f.type || 'text'}
                  value={form[f.key] || ''}
                  onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                  placeholder={f.placeholder}
                />
              </div>
            ))}
          </div>

          <p className="ap-section-title">Cover Image</p>
          <div className="ap-img-upload-area" onClick={() => coverInputRef.current.click()}>
            <input ref={coverInputRef} type="file" accept="image/*" onChange={handleCover} />
            {coverPreview
              ? <img src={coverPreview} alt="cover" style={{ maxHeight: 120, borderRadius: 8 }} />
              : <><div style={{ fontSize: '1.8rem' }}>🖼️</div><div>Click to select cover image</div></>
            }
          </div>

          <p className="ap-section-title">Product Images</p>
          <div className="ap-img-upload-area" onClick={() => prodInputRef.current.click()}>
            <input ref={prodInputRef} type="file" accept="image/*" multiple onChange={handleProductImages} />
            <div style={{ fontSize: '1.8rem' }}>📷</div>
            <div>Click to add product images</div>
            <div className="ap-img-upload-hint">Multiple allowed</div>
          </div>
          {productPreviews.length > 0 && (
            <div className="ap-img-previews">
              {productPreviews.map((src, i) => (
                <div className="ap-img-thumb" key={i}>
                  <img src={src} alt={`prod-${i}`} />
                  <button type="button" className="ap-img-thumb-remove" onClick={() => removeProductImage(i)}>✕</button>
                </div>
              ))}
            </div>
          )}

          {uploading && (
            <div className="ap-progress-wrap" style={{ marginTop: 16 }}>
              <div className="ap-progress-label">Uploading… {progress}%</div>
              <div className="ap-progress-bar"><div className="ap-progress-fill" style={{ width: `${progress}%` }} /></div>
            </div>
          )}

          <div className="ap-btn-row">
            <button type="button" className="ap-btn ap-btn-ai" style={{ display:'flex', alignItems:'center', gap:6 }} onClick={() => setShowPopulate(true)}>
              <Wand2 size={15} /> AI auto fill
            </button>
            <button type="submit" className="ap-btn ap-btn-primary" disabled={uploading} style={{ display:'flex', alignItems:'center', gap:6 }}>
              <Monitor size={16} /> {uploading ? 'Uploading…' : 'Add Desktop Tower'}
            </button>
            <button type="button" className="ap-btn ap-btn-ghost" onClick={() => navigate('/admin/dashboard')}>Cancel</button>
          </div>
        </form>
      </div>

      {showPopulate && (
        <PopulateValuesModal onClose={() => setShowPopulate(false)} onApply={handlePopulate} targetFields={AI_TARGET_FIELDS} />
      )}

      {showConfirm && (
        <ConfirmDialog
          title="Add this desktop tower?"
          message={`"${form.Name}" — ₹${Number(form.Price).toLocaleString('en-IN')}`}
          confirmLabel="Yes, Add Desktop"
          confirmingLabel="Adding…"
          confirming={uploading}
          onConfirm={handleSubmit}
          onCancel={() => setShowConfirm(false)}
        />
      )}
    </div>
  );
}
